import React from 'react'
import {
  CheckCircle2,
  ShieldCheck,
  Wallet,
  Shield,
  Fingerprint,
  ArrowRight,
} from 'lucide-react'

export type OnboardingWelcomeScreenProps = {
  beamioTag?: string
  onEnterHome: () => void
}

/**
 * Post Security Backup — welcomrPage.html style success / welcome.
 */
export default function OnboardingWelcomeScreen({ beamioTag, onEnterHome }: OnboardingWelcomeScreenProps) {
  const handle = (beamioTag || '').replace(/^@+/, '').trim()
  const displayHandle = handle ? `@${handle}` : '@you'

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden bg-[#f9f9fe] text-[#1a1c1f]">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute left-[-10%] top-[-10%] h-[40%] w-[40%] rounded-full bg-[#004bc3]/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] h-[40%] w-[40%] rounded-full bg-[#465c99]/5 blur-[120px]" />
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(circle at 50% 50%, rgba(21, 98, 240, 0.08) 0%, rgba(249, 249, 254, 0) 70%)',
          }}
        />
      </div>

      <main className="relative z-10 mx-auto flex min-h-0 w-full max-w-lg flex-1 flex-col items-center justify-center overflow-y-auto overscroll-contain px-6 py-8 text-center">
        <div className="relative mb-10 shrink-0 md:mb-12">
          <div
            className="relative flex h-40 w-40 items-center justify-center overflow-hidden rounded-full bg-white/40 shadow-[0_8px_32px_rgba(21,98,240,0.1)] backdrop-blur-xl md:h-56 md:w-56"
            style={{ WebkitBackdropFilter: 'blur(20px)' }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#004bc3]/10 to-transparent" />
            <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-[#004bc3] to-[#1562f0] text-white shadow-xl md:h-32 md:w-32">
              <CheckCircle2 className="h-12 w-12 md:h-16 md:w-16" strokeWidth={2.25} aria-hidden />
            </div>
            <div className="absolute left-8 top-4 h-2 w-2 rounded-full bg-[#dbe1ff] opacity-60" />
            <div className="absolute bottom-10 right-4 h-3 w-3 rounded-full bg-[#dbe1ff] opacity-40" />
            <div className="absolute left-4 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-[#ffdbce] opacity-50" />
          </div>
          <div
            className="absolute -right-6 -top-6 flex h-12 w-12 rotate-12 items-center justify-center rounded-lg bg-white/80 shadow-sm backdrop-blur-xl"
            style={{ WebkitBackdropFilter: 'blur(20px)' }}
          >
            <ShieldCheck className="h-7 w-7 text-[#004bc3]" strokeWidth={2} />
          </div>
          <div
            className="absolute -bottom-2 -left-8 flex h-14 w-14 -rotate-12 items-center justify-center rounded-full bg-white/80 shadow-sm backdrop-blur-xl"
            style={{ WebkitBackdropFilter: 'blur(20px)' }}
          >
            <Wallet className="h-7 w-7 text-[#465c99]" strokeWidth={2} />
          </div>
        </div>

        <div className="max-w-2xl shrink-0 space-y-4">
          <h1 className="text-5xl font-extrabold leading-[1.1] tracking-tighter text-[#1a1c1f] md:text-7xl">
            Welcome, <span className="text-[#004bc3]">{displayHandle}</span>
          </h1>
          <p className="mx-auto max-w-md text-lg font-medium leading-relaxed text-[#424655] md:text-xl">
            Your high-security digital perimeter is now active and ready for your first transaction.
          </p>
        </div>

        <div className="mt-10 grid w-full max-w-md shrink-0 grid-cols-2 gap-4 md:mt-12">
          <div className="flex flex-col gap-2 rounded-lg bg-[#f3f3f8] p-5 text-left">
            <Shield className="h-6 w-6 text-[#004bc3]" strokeWidth={2} aria-hidden />
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-[#424655]">Security Status</p>
              <p className="font-semibold text-[#1a1c1f]">Fortified</p>
            </div>
          </div>
          <div className="flex flex-col gap-2 rounded-lg bg-[#f3f3f8] p-5 text-left">
            <Fingerprint className="h-6 w-6 text-[#465c99]" strokeWidth={2} aria-hidden />
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-[#424655]">Biometrics</p>
              <p className="font-semibold text-[#1a1c1f]">Enabled</p>
            </div>
          </div>
        </div>

        <div className="mt-12 w-full max-w-sm shrink-0 space-y-4 pb-[env(safe-area-inset-bottom)] md:mt-16">
          <button
            type="button"
            onClick={onEnterHome}
            className="flex h-16 w-full items-center justify-center gap-3 rounded-full bg-gradient-to-br from-[#004bc3] to-[#1562f0] text-lg font-bold text-white shadow-[0_8px_24px_rgba(21,98,240,0.25)] transition-all hover:opacity-90 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004bc3]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#f9f9fe]"
          >
            Enter Home
            <ArrowRight className="h-6 w-6" strokeWidth={2.25} aria-hidden />
          </button>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#424655]">Next: Secure Account Activation</p>
        </div>
      </main>

      {/* Bottom activation peek */}
      <div className="pointer-events-none fixed bottom-0 left-0 z-30 h-2 w-full shrink-0 bg-[#e8e8ed] shadow-[0_-4px_24px_rgba(0,0,0,0.04)]">
        <div
          className="absolute left-1/2 top-0 flex -translate-x-1/2 -translate-y-full items-center gap-2 rounded-t-xl bg-white px-4 py-1.5 shadow-sm backdrop-blur-xl"
          style={{ WebkitBackdropFilter: 'blur(20px)' }}
        >
          <div className="h-2 w-2 rounded-full bg-[#004bc3]" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#424655]">Activation Pending</span>
        </div>
      </div>
    </div>
  )
}
